// Layer 3 (Transformation): a project's files (polish.P23 #710, comp
// `project-home.html` adjudication 5). The lead-zinc concentrator's files are
// the ingestion seed's artefacts — the Ingestion ledger's own rows — so the
// project's files are what its plant model is built from, and its file count
// is derived from that ledger, never typed. Projects without an ingestion seed
// keep their seeded count.

import type { ProjectTile } from '@/versions/v1/shared/project-seed';
import {
  bunkerHillSources,
  contributionCards,
  ledgerRows,
  type LedgerExtent,
} from '@/versions/v1/modules/ingestion/SetupScene.transform';

/** The one project the ingestion seed (`SOURCE_DOCUMENTS`) describes. */
export const INGESTION_PROJECT_SLUG = 'alpha';

export type ProjectFileRow = {
  id: string;
  title: string;
  fullTitle: string;
  inModel: boolean;
  stateLabel: string;
  /** What the artefact gives the model, in the ledger's short form (`put`). */
  contribution: string;
  /** The one figure the artefact adds and its noun (`248%` `circulating load`). */
  extent: LedgerExtent;
};

export type ProjectFilesPanel = {
  rows: readonly ProjectFileRow[];
  total: number;
  inModel: number;
};

/** The Files panel: the ingestion seed's artefacts (the Ingestion ledger's
 *  rows), so the project's files are what the plant model is built from. */
export function projectFilesPanel(projectSlug: string): ProjectFilesPanel {
  const cards = contributionCards(bunkerHillSources(), new Set(), projectSlug);
  const rows = ledgerRows(cards, new Set()).map((row) => ({
    id: row.id,
    // The artefact column names the kind (`Technical paper`); the document's own
    // title rides along as the row's tooltip.
    title: row.kindPrefix ?? row.title,
    fullTitle: row.kindPrefix ? `${row.kindPrefix} · ${row.title}` : row.title,
    inModel: row.status === 'present',
    stateLabel: row.status === 'present' ? 'In model' : row.statusLabel,
    // The ledger's short fields, so each fact reads once on the page: the
    // balance's extent is its circulating load, not the design basis the
    // header already states, and the TARP's count sits only in its extent.
    contribution: row.put,
    extent: row.extent,
  }));
  return { rows, total: rows.length, inModel: rows.filter((row) => row.inModel).length };
}

/** Files shown for a project: the ingestion ledger's length for the seeded
 *  plant, the tile's own seeded count otherwise (0 for a new project). */
export function projectFileCount(tile: Pick<ProjectTile, 'slug' | 'fileCount'>): number {
  return tile.slug === INGESTION_PROJECT_SLUG ? projectFilesPanel(tile.slug).total : (tile.fileCount ?? 0);
}
